// libs
import get from 'lodash/get';

// helpers
import { getCouponCodes } from './helper';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// defs
import {
  ApplyCouponCode,
  CouponsList,
  Coupon,
  COUPON_INTERACTION_LOCATION,
  COUPON_TYPE,
  COUPON_PAGE_TYPE,
  ApplyCouponCodeRemove
} from './types';

// constants
import { COUPON_EVENTS } from 'shared/helpers/analytics/constants/coupon';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';

export const getPageData = (couponData: CouponsList, isEligibleForDeltaCommunication: boolean) => {
  const appliedCoupon = get(couponData, 'appliedCoupon', []);
  const applicableCoupons = get(couponData, 'applicableCoupons', []);
  const nonApplicableCoupons = get(couponData, 'nonApplicableCoupons', []);
  let couponTypeDetails: any[] = [];
  try {
    const map = new Map();

    for (const offer of [...appliedCoupon, ...applicableCoupons, ...nonApplicableCoupons]) {
      const coupon_type = offer.offerType || '';
      const funding_type = offer?.fundingType || '';

      const key = `${coupon_type}::${funding_type}`;
      const isApplied = offer?.couponStatus === 'APPLIED' || offer?.couponStatus === 'APPLICABLE';

      if (!map.has(key)) {
        map.set(key, {
          coupon_type,
          funding_type,
          appliedcount: 0,
          nonappliedcount: 0,
          delcomcount: 0,
          totalcount: 0,
        });
      }

      const group = map.get(key);
      group.totalcount += 1;

      if (isApplied) {
        group.appliedcount += 1;
      } else {
        group.nonappliedcount += 1;
        if (offer.deltaAmount !== null && offer.deltaAmount !== undefined) {
          group.delcomcount += 1;
        }
      }
    }

    couponTypeDetails = Array.from(map.values());

    const couponsMetaDetails = [
      ...appliedCoupon,
      ...applicableCoupons,
      ...nonApplicableCoupons,
    ].map((coupon) => {
      const {
        couponCode,
        couponStatus,
        fundingType,
        deltaAmount,
        deltaQuantity,
        deltaTextModified,
      } = coupon;
      let deltaComEligible = 'na';
      if (isEligibleForDeltaCommunication) {
        deltaComEligible = 'eligible';
        if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
          deltaComEligible = 'na';
        }
      }
      if (deltaTextModified) {
        deltaComEligible = 'available';
      }

      let deltaComTypeTosend = 'na';
      if (Number(deltaAmount) > 0) {
        deltaComTypeTosend = 'price';
      } else if (deltaQuantity > 0) {
        deltaComTypeTosend = 'quantity';
      } else if (deltaTextModified) {
        deltaComTypeTosend = 'text';
      }

      return {
        coupon_code: couponCode,
        coupon_status: couponStatus,
        coupon_type: coupon.offerType,
        offer_id: coupon.offerId,
        funding_type: fundingType || 'na',
        deltacomtype: deltaComTypeTosend,
        deltacom: deltaComEligible,
      };
    });

    const couponCodes = getCouponCodes([
      ...appliedCoupon,
      ...applicableCoupons,
      ...nonApplicableCoupons,
    ]);
    const totalCouponCount = couponCodes.length;

    return {
      couponCodes: couponCodes.join(','),
      totalCouponCount,
      couponsMetaDetailsCart: couponsMetaDetails,
      couponTypeDetailsCart: couponTypeDetails,
      couponDetails: `${totalCouponCount}|applied: ${appliedCoupon.length}, applicable: ${applicableCoupons.length}, nonApplicable: ${nonApplicableCoupons.length}`,
    };
  } catch {
    // logger
  }
};

// coupons page view
export const onCouponPageLoad = (
  couponData: CouponsList,
  isEligibleForDeltaCommunication: boolean
) => {
  const pageData = getPageData(couponData, isEligibleForDeltaCommunication);
  try {
    NFA.track({
      data: {
        ...pageData,
        interaction_location: COUPON_INTERACTION_LOCATION.CART_PAGE_L2,
        page: 'coupon_page',
        page_type: 'coupon_page',
      },
      event: COUPON_EVENTS.COUPON_PAGE_LOADS,
      type: TRACK_TYPE.PAGELOAD,
    });
  } catch {
    // logger
  }
};

export const onCouponApplyClicked = ({
  couponCode,
  isCouponCodeEnteredManually,
  bestCouponTag,
}: ApplyCouponCode) => {
  try {
    NFA.track({
      data: {
        coupon_code: couponCode,
        is_entered_manually: isCouponCodeEnteredManually,
        interaction_location: COUPON_INTERACTION_LOCATION.COUPON_PAGE,
        pageview_location: 'coupon_page',
        best_coupon_tag: bestCouponTag || 'UNCATEGORIZED',
      },
      event: COUPON_EVENTS.COUPON_COLLECTED,
    });
  } catch {
    // logger
  }
};

export const onCouponApplyFailure = ({
  couponCode,
  failureReason,
}: {
  couponCode: string;
  failureReason?: string;
}) => {
  try {
    NFA.track({
      data: { couponCode, failureReason },
      event: COUPON_EVENTS.COUPON_APPLY_FAILURE,
    });
  } catch {
    // logger
  }
};

export const onCouponApplyRemoved = ({ couponCode, isRemovedManually, bestCouponTag, interactionLocation = COUPON_INTERACTION_LOCATION.CART_PAGE }: ApplyCouponCodeRemove) => {
  try {
    NFA.track({
      data: {
        coupon_code: couponCode,
        is_removed_manually: isRemovedManually,
        interaction_location: interactionLocation,
        pageview_location: interactionLocation === COUPON_INTERACTION_LOCATION.COUPON_PAGE ? 'coupon_page' : 'cart_page',
        source_platform: 'web',
        best_coupon_tag: bestCouponTag || 'UNCATEGORIZED',
      },
      event: COUPON_EVENTS.COUPON_REMOVED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user collects the coupon
export const onCouponCollected = ({ couponCode, isCouponCodeEnteredManually, bestCouponTag }: ApplyCouponCode) => {
  try {
    if (!couponCode) return;

    NFA.track({
      data: {
        coupon_code: couponCode,
        is_entered_manually: isCouponCodeEnteredManually,
        interaction_location: COUPON_INTERACTION_LOCATION.COUPON_PAGE,
        pageview_location: 'coupon_page',
        best_coupon_tag: bestCouponTag ?? 'UNCATEGORIZED',
      },
      event: COUPON_EVENTS.COUPON_COLLECTED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user clicks on see details of the coupon
export const onCouponSeeDetailsClicked = (
  couponDetails: Coupon,
  isEligibleForDeltaCommunication: boolean
) => {
  try {
    const {
      offerType,
      offerId,
      couponDetails: { couponStatus, fundingType, couponCode },
      deltaAmount,
      deltaQuantity,
      deltaTextModified,
    } = couponDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      if (deltaTextModified) deltaComEligible = 'eligible';
      if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
        deltaComEligible = 'available';
      }
    }

    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType || 'na',
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      deltacom: deltaComEligible,
      interactionLocation: COUPON_INTERACTION_LOCATION.PDP,
      deltacomType: deltaComTypeTosend,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType,
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_SEE_DETAILS_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user clicks on see details of the coupon
export const onCouponSeeDetailsClickedCart = (
  couponDetails: Coupon,
  isEligibleForDeltaCommunication: boolean
) => {
  try {
    const {
      offerType,
      offerId,
      couponStatus,
      fundingType,
      couponCode,
      deltaAmount,
      deltaQuantity,
      deltaTextModified,
    } = couponDetails;
    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      if (deltaTextModified) deltaComEligible = 'eligible';
      if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
        deltaComEligible = 'available';
      }
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType || 'na',
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      deltacom: deltaComEligible,
      interactionLocation: COUPON_INTERACTION_LOCATION.COUPON_PAGE,
      deltacomType: deltaComTypeTosend,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType: fundingType || 'na',
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_SEE_DETAILS_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user clicks on see details of offer
export const onOfferSeeDetailsClicked = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean
) => {
  try {
    const {
      offerType,
      offerId,
      status,
      deltaTextModified,
      fundingType = 'na',
      deltaAmount = '',
      deltaQuantity = '',
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      deltaComEligible = 'eligible';
    }
    if (isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';

    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    const couponMetaDetails = {
      offer_id: offerId,
      offer_status: status,
      offer_type: offerType,
      funding_type: fundingType,
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      offer_id: offerId,
      offer_status: status,
      offer_type: offerType,
      interactionLocation: COUPON_INTERACTION_LOCATION.PDP,
      deltacomType: deltaComTypeTosend,
      offersMetaDetailsSingle: [couponMetaDetails],
      fundingType,
      deltacom: deltaComEligible,
      deltacomType: deltaComTypeTosend,
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.OFFERS_SEE_DETAILS_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user click on coupon shop more
export const onCouponShopMoreClicked = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean,
  isDetailsBottomSheet: boolean = false
) => {
  try {
    const {
      offerType,
      offerId,
      deltaAmount,
      deltaQuantity,
      couponDetails: { couponStatus, fundingType, couponCode },
      deltaTextModified,
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      deltaComEligible = 'eligible';
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    let interactionLocationToSend: string = COUPON_INTERACTION_LOCATION.PDP;
    if (isDetailsBottomSheet) {
      interactionLocationToSend = `${interactionLocationToSend}:coupon:BS`;
    } else {
      interactionLocationToSend = `${interactionLocationToSend}:coupon`;
    }

    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType || 'na',
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      deltacom: deltaComEligible,
      interactionLocation: interactionLocationToSend,
      deltacomType: deltaComTypeTosend,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType,
      siteNavigation: 'coupon_offers_page',
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_SHOP_MORE_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user click on coupon shop more
export const onCouponShopMoreClickedCart = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean,
  isClickedFromBottomSheet: boolean = false
) => {
  try {
    const {
      offerType,
      offerId,
      couponStatus,
      fundingType,
      couponCode,
      deltaAmount,
      deltaQuantity,
      deltaTextModified,
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      if (deltaTextModified) deltaComEligible = 'eligible';
      if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
        deltaComEligible = 'available';
      }
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    let interactionLocationToSend: string = COUPON_INTERACTION_LOCATION.COUPON_PAGE;
    if (isClickedFromBottomSheet) {
      interactionLocationToSend = `${interactionLocationToSend}:BS`;
    }

    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType || 'na',
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      deltacom: deltaComEligible,
      interactionLocation: interactionLocationToSend,
      deltacomType: deltaComTypeTosend,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType: fundingType || 'na',
      siteNavigation: 'coupon_offers_page',
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_SHOP_MORE_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user click on offer shop more
export const onOfferShopMoreClicked = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean,
  isDetailsView: boolean = false
) => {
  try {
    const {
      offerType,
      offerId,
      status,
      deltaTextModified,
      fundingType = 'na',
      deltaAmount = '',
      deltaQuantity = '',
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      deltaComEligible = 'eligible';
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (deltaAmount) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }

    const couponMetaDetails = {
      offer_id: offerId,
      offer_status: status,
      offer_type: offerType,
      funding_type: fundingType,
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };

    let interactionLocationToSend: string = COUPON_INTERACTION_LOCATION.PDP;

    if (isDetailsView) {
      interactionLocationToSend = `${interactionLocationToSend}:offer:BS`;
    } else {
      interactionLocationToSend = `${interactionLocationToSend}:offer`;
    }

    const couponDataToTrack = {
      offer_id: offerId,
      offer_status: status,
      offer_type: offerType,
      interactionLocation: interactionLocationToSend,
      deltacomType: deltaComTypeTosend,
      offersMetaDetailsSingle: [couponMetaDetails],
      fundingType,
      deltacom: deltaComEligible,
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.OFFERS_SHOP_MORE_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user click on offer shop more
export const onCouponCodeCopyClicked = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean,
  isDetailsBottomSheet: boolean = false
) => {
  try {
    const {
      offerType,
      offerId,
      deltaAmount,
      deltaTextModified,
      deltaQuantity,
      couponDetails: { couponStatus, fundingType, couponCode },
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      if (deltaTextModified) deltaComEligible = 'eligible';
      if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
        deltaComEligible = 'available';
      }
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }
    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType,
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    let interactionLocationToSend: string = COUPON_INTERACTION_LOCATION.PDP;
    if (isDetailsBottomSheet) {
      interactionLocationToSend = `${interactionLocationToSend}:coupon:BS`;
    } else {
      interactionLocationToSend = `${interactionLocationToSend}:coupon`;
    }

    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      interactionLocation: interactionLocationToSend,
      deltacomType: deltaComTypeTosend,
      deltacom: deltaComEligible,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType,
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_COPY_CODE_CLICKED,
    });
  } catch {
    // logger
  }
};

// this is triggered when user click on offer shop more
export const onCouponCodeCopyClickedCart = (
  offerDetails: Coupon,
  isEligibleForDeltaCommunication: boolean,
  isClickedFromBottomSheet: boolean = false
) => {
  try {
    const {
      offerType,
      offerId,
      deltaAmount,
      deltaTextModified,
      deltaQuantity,
      couponStatus,
      fundingType = 'na',
      couponCode,
    } = offerDetails;

    let deltaComEligible = 'na';
    if (isEligibleForDeltaCommunication) {
      if (deltaTextModified) deltaComEligible = 'eligible';
      if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
        deltaComEligible = 'available';
      }
    }
    if (deltaTextModified) {
      deltaComEligible = 'available';
    }

    if (!isEligibleForDeltaCommunication && deltaTextModified) {
      deltaComEligible = 'eligible';
    }

    let deltaComTypeTosend = 'na';
    if (Number(deltaAmount) > 0) {
      deltaComTypeTosend = 'price';
    } else if (deltaQuantity > 0) {
      deltaComTypeTosend = 'quantity';
    } else if (deltaTextModified) {
      deltaComTypeTosend = 'text';
    }
    let interactionLocationToSend: string = COUPON_INTERACTION_LOCATION.COUPON_PAGE;
    if (isClickedFromBottomSheet) {
      interactionLocationToSend = `${interactionLocationToSend}:BS`;
    }
    const couponMetaDetails = {
      coupon_code: couponCode,
      coupon_status: couponStatus,
      coupon_type: offerType,
      offer_id: offerId,
      funding_type: fundingType || 'na',
      deltacomtype: deltaComTypeTosend,
      deltacom: deltaComEligible,
    };
    const couponDataToTrack = {
      couponCode,
      couponType: offerType,
      offerId,
      couponStatus,
      interactionLocation: interactionLocationToSend,
      deltacomType: deltaComTypeTosend,
      deltacom: deltaComEligible,
      couponsMetaDetailsSingle: [couponMetaDetails],
      fundingType: fundingType || 'na',
    };
    NFA.track({
      data: couponDataToTrack,
      event: COUPON_EVENTS.COUPON_COPY_CODE_CLICKED,
    });
  } catch {
    // logger
  }
};

// eslint-disable-next-line  no-undef
const getGroupedOffersTypeData = (offers: offerDetails[]) => {
  const offers_type_details = Object.values(
    offers.reduce((acc, offer) => {
      const type = offer.offerType || 'UNKNOWN';
      const status = offer.status;

      if (!acc[type]) {
        acc[type] = {
          offer_type: type,
          appliedcount: 0,
          nonappliedcount: 0,
          delcomcount: 0, // number of offers that are not applied but deltaAmount is non-null
          totalcount: 0,
        };
      }

      acc[type].totalcount += 1;

      if (status === 'APPLIED') {
        acc[type].appliedcount += 1;
      } else {
        acc[type].nonappliedcount += 1;
      }

      if (status !== 'APPLIED' && offer.deltaAmount !== null) {
        acc[type].delcomcount += 1;
      }

      return acc;
    }, {})
  );
  return offers_type_details;
};

// eslint-disable-next-line  no-undef
const getGroupedCouponsData = (offers: offerDetails[]) => {
  const offers_type_details = Object.values(
    offers.reduce((acc, offer) => {
      const type = offer.offerType || 'UNKNOWN';
      const couponStatus = offer?.couponDetails?.couponStatus;
      const isApplied = offer.status === 'APPLIED' || couponStatus === 'APPLICABLE';

      if (!acc[type]) {
        acc[type] = {
          coupon_type: type,
          appliedcount: 0,
          nonappliedcount: 0,
          delcomcount: 0,
          totalcount: 0,
        };
      }

      acc[type].totalcount += 1;

      if (isApplied) {
        acc[type].appliedcount += 1;
      } else {
        acc[type].nonappliedcount += 1;
      }

      if (!isApplied && offer.deltaAmount !== null) {
        acc[type].delcomcount += 1;
      }

      return acc;
    }, {})
  );

  return offers_type_details;
};

export const onCouponsDetailsLoaded = (
  offerDetails: Coupon[],
  couponDetails: Coupon[],
  isEligibleForDeltaCommunication: boolean
) => {
  let couponTypeDetails: any[] = [];
  let offerTypeDetails: any[] = [];
  try {
    couponTypeDetails = getGroupedCouponsData(couponDetails);
    const couponsMetaDetails = couponDetails.map((coupon) => {
      const { couponCode, couponStatus, fundingType } = coupon.couponDetails;
      const { deltaTextModified, deltaAmount, deltaQuantity } = coupon;

      let deltaComEligible = 'na';
      if (isEligibleForDeltaCommunication) {
        deltaComEligible = 'eligible';
        if (couponStatus === 'APPLIED' || couponStatus === 'APPLICABLE') {
          deltaComEligible = 'available';
        }
      }
      if (deltaTextModified) {
        deltaComEligible = 'available';
      }

      let deltaComTypeTosend = 'na';
      if (Number(deltaAmount) > 0) {
        deltaComTypeTosend = 'price';
      } else if (deltaQuantity > 0) {
        deltaComTypeTosend = 'quantity';
      } else if (deltaTextModified) {
        deltaComTypeTosend = 'text';
      }

      return {
        coupon_code: couponCode,
        coupon_status: couponStatus,
        coupon_type: coupon.offerType,
        offer_id: coupon.offerId,
        funding_type: fundingType,
        deltacomtype: deltaComTypeTosend,
        deltacom: deltaComEligible,
      };
    });

    offerTypeDetails = getGroupedOffersTypeData(offerDetails);

    const offersMetaDetails = offerDetails.map((offer) => {
      const { deltaAmount, deltaQuantity, deltaTextModified } = offer;

      let deltaComEligible = 'na';
      if (isEligibleForDeltaCommunication) {
        deltaComEligible = 'eligible';
      }
      if (isEligibleForDeltaCommunication && deltaTextModified) {
        deltaComEligible = 'available';
      }

      if (!isEligibleForDeltaCommunication && deltaTextModified) {
        deltaComEligible = 'eligible';
      }

      let deltaComTypeTosend = 'na';

      if (Number(deltaAmount) > 0) {
        deltaComTypeTosend = 'price';
      } else if (deltaQuantity > 0) {
        deltaComTypeTosend = 'quantity';
      } else if (deltaTextModified) {
        deltaComTypeTosend = 'text';
      }
      return {
        offer_id: offer.offerId,
        offer_status: offer.offerStatus || 'na',
        deltacom: deltaComEligible,
        deltacomtype: deltaComTypeTosend,
        offer_type: offer.offerType,
        funding_type: offer.fundingType || 'na',
      };
    });

    const couponDeltaCount = couponTypeDetails.filter((c) => c.deltacom).length;
    const offerDeltaCount = offerTypeDetails.filter((o) => o.deltacom).length;

    const couponSummary = {
      totalcount: couponDetails.length,
      applicablecount: couponDetails.length,
      delcomcount: couponDeltaCount,
    };

    const offerSummary = {
      totalcount: offerDetails?.length,
      nonappliedcount: offerDetails?.length,
      appliedcount: 0,
      delcomcount: offerDeltaCount,
    };

    const dataToTrack = {
      interactionLocation: COUPON_INTERACTION_LOCATION.PDP,
      couponSummary,
      offerSummary,
      couponTypeDetails,
      offerTypeDetails,
      couponsMetaDetails,
      offersMetaDetails,
    };

    NFA.updateDataLayer({
      ...dataToTrack,
    });
  } catch {
    // logger
  }
};

export const onViewProductsClicked = ({
  couponType,
  couponClickType,
}: {
  couponType: string;
  couponClickType: string;
}) => {
  NFA.updateDataLayer({
    pageType: couponType,
    offerPageType:
      couponType == COUPON_TYPE.COUPON
        ? COUPON_PAGE_TYPE.COUPON_LISTING_TYPE
        : COUPON_PAGE_TYPE.OFFER_LISTING_TYPE,
    siteSubProductNavigation: couponClickType,
  });
};
